import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import { observer } from 'mobx-react-lite'
import { colors, device, spacing } from '../../styles/_var'
import { CompetitionBody, TabSection } from '../CompetitionPage/CompetitionPage'
import AboutCompetition from '../CompetitionPage/AboutCompetition'
import CompetitionBanner from '../CompetitionPage/CompetitionBanner'
import Text from '../../components/Text'
import Input from '../../components/Input'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import TextArea from '../../components/TextArea'
import AddContestantForm from '../../components/AddContestantForm'
import { CompetitionPageWrapper } from './StyledComponents'
import seasonStore from '../../stores/seasonStore'
import votingRoundStore from '../../stores/votingRoundStore'
import contestantStore from '../../stores/contestantStore'
import usePageTitle from '../../hooks/usePageTitle'
import TabContent from './components/TabContent'
import competitionDashboardStore from '../../stores/competitionDashboardStore'
import { CONTESTANT_TAB } from '../../stores/competitionPageStore'
import { ContestantWithBio } from '../../utils/interfaces'
import uiStore from '../../stores/uiStore'


const CompetitionDashboard: FunctionComponent = () => {
  const { name, seasons, username, banner, logo, visibility } = competitionDashboardStore.data.competitionDetails
  const {concluded, description, uuid: seasonId} = seasonStore.selectedSeason
  usePageTitle('Competition Dashboard')

  const onSaveContestant = async(payload: ContestantWithBio) => {
    if (!competitionDashboardStore.selectedCategory) return uiStore.errorModal('Please select a category to add a contestant.');

    contestantStore.saveContestant({
      payload,
      creatingCompetition: false,
      currentSeasonId: seasonId,
      subscriptionPlan: competitionDashboardStore.subscriptionPlan,
      categoryId: competitionDashboardStore.selectedCategory?.id
    })
  }

  return (
    <CompetitionPageWrapper>
      <CompetitionBanner
        seasonCompleted={concluded}
        endCompetitionAction={() => competitionDashboardStore.closeSeason(seasonId)}
        onBannerImageChange={image => competitionDashboardStore.updateFields('banner', image)}
        onLogoImageChange={image => competitionDashboardStore.updateFields('logo', image)}
        onClickEdit={() => competitionDashboardStore.onEditName()}
        banner={banner}
        logo={logo}
        seasons={seasons}
        username={username}
        name={name}
        visibility={visibility}
        editMode 
      />
      <AboutCompetition
        onClickEdit={() => competitionDashboardStore.onEditDescription()}
        about={description}
        editMode 
      />
      <CompetitionBody role="tab">
        <TabbedSection role="tabpanel">
          {competitionDashboardStore.tabLabels.map((tab, index) => (
            <TabItem
              onClick={() => competitionDashboardStore.setSelectedTab(tab)}
              key={index}
              selected={tab === competitionDashboardStore.selectedTab}
            >
              <Text variant="regular" truncate>{tab}</Text>
            </TabItem>
          ))}
        </TabbedSection>
        <TabBody role="tablist">
          <BodyWrapper>
            <TabContent 
              selectedTab={competitionDashboardStore.selectedTab}
              contestantLabel={CONTESTANT_TAB}
              tabs={competitionDashboardStore.tabs}
              votingOptions={votingRoundStore.lastVotingRound?.voting_option}
              deleteContestant={contestantUsername => competitionDashboardStore.deleteContestant(contestantUsername)}
              evictContestant={contestantUsername => competitionDashboardStore.evictContestant(contestantUsername)}
              saveContestant={payload => onSaveContestant(payload)}
              savingContestant={contestantStore.savingContestant}
              contestants={contestantStore.getContestantFilteredByCategory(competitionDashboardStore.selectedCategory)}
              onClickContestantButton={() => competitionDashboardStore.onClickContestantButton()}
            />
          </BodyWrapper>
        </TabBody>
      </CompetitionBody>
      <Modal 
        onClose={() => competitionDashboardStore.onCloseEditModal()} 
        showModal={!!competitionDashboardStore.editField}
      >
        <FormWrapper>
          {competitionDashboardStore.editField === 'description' ? (
            <TextArea
              onFocus={() => competitionDashboardStore.clearInputErrors()}
              errorMessage={competitionDashboardStore.editInputError}
              placeholder={`update ${competitionDashboardStore.editField}`}
              value={competitionDashboardStore.editValue}
              onChange={e => competitionDashboardStore.setEditValue(e.target.value)} />
          ) : (
            <Input
              onFocus={() => competitionDashboardStore.clearInputErrors()}
              errorMessage={competitionDashboardStore.editInputError}
              placeholder={`update ${competitionDashboardStore.editField}`}
              value={competitionDashboardStore.editValue}
              onChange={e => competitionDashboardStore.setEditValue(e.target.value)} />
          )}
          <Button mt={2} invertOnHover onClick={() => competitionDashboardStore.updateFields()}>Update</Button>
        </FormWrapper>
      </Modal>
      <ContestantModal
        onClose={() => competitionDashboardStore.setShowAddContestantForm(false)}
        showModal={competitionDashboardStore.showAddContestantForm}>
        <Text variant="medium" mb={2} center>Add New Contestant</Text>
        <AddContestantForm
          submit={(payload, onComplete) => competitionDashboardStore.onAddContestant(payload, onComplete)}
          submitting={contestantStore.savingContestant}
          onAvatarUploaded={avatar => competitionDashboardStore.setContestantAvatar(avatar)}
        />
      </ContestantModal>
    </CompetitionPageWrapper>
  )
}

export default observer(CompetitionDashboard)

const FormWrapper = styled.div`
  text-align: center;
`

export const TabItem = styled(({ selected, ...rest }) => <div {...rest} />)`
  text-decoration: none;
  color: ${colors.black};
  width: 30%;
  max-width: 30rem;
  cursor: pointer;
  text-align: center;
  border-bottom: solid 2px ${colors.lightgrey};
  text-transform: uppercase;
  transition: all ease .2s;
  margin-right: ${spacing.rg};

  &:hover {
    color: ${colors.purple};
    border-bottom-color: ${colors.purple};
  }

  ${({ selected }) => selected && `
    color: ${colors.purple};
    border-bottom-color: ${colors.purple};
  `}
`

const BodyWrapper = styled.div`
  padding: ${spacing.xs} ${spacing.sm};
`

export const AddContestantButton = styled(({ ...rest }) => <Button {...rest} />)`
  padding-left: ${spacing.xs};
  padding-right: ${spacing.xs};
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    margin-right: ${spacing.xxs};
  }
`

const ContestantModal = styled(({ ...rest }) => <Modal {...rest} />)`
  max-width: unset;
  width: 75%;

  @media ${device.tablet} {
    min-width: 70rem;
    max-width: 80rem;
    width: 80%
  }

  & > div:last-child {
    overflow: scroll;
    max-height: 70vh;
  }
`

const TabbedSection = styled(TabSection)`
  justify-content: flex-start;
`

const TabBody = styled.div`
box-sizing: border-box;
padding: 0 ${spacing.sm};
`